import { css, html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { LocalDate } from 'src/utilities/local-date.js';
import { t } from 'src/i18n';
import { overdueIssues } from './issue-escalation-overdue';
import type { IssueEscalationCandidate } from './issue-escalation';

/**
 * Oversiktswidget (krav 1.2.1): viser antall avvik som er over frist.
 *
 * Vises kun når organisasjonen har tatt i bruk frist (`enabled`) og det
 * faktisk finnes avvik over frist.
 */
@customElement('d-issue-escalation-widget')
export class DIssueEscalationWidget extends LitElement {
  static readonly styles = css`
    :host {
      display: block;
    }
    .widget {
      display: flex;
      align-items: center;
      gap: 12px;
      margin: 6px 0;
      padding: 12px var(--listPaddingLeft, 12px);
      border-radius: 8px;
      background: var(--alertColorLizzaBackground, hsl(8, 80%, 96%));
      border: 1px solid var(--alertColor, #c0392b);
      color: inherit;
      text-decoration: none;
      cursor: pointer;
    }
    .count {
      flex: none;
      min-width: 34px;
      height: 34px;
      border-radius: 50%;
      background: var(--alertColor, #c0392b);
      color: white;
      font-weight: bold;
      font-size: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .text {
      font-weight: bold;
    }
    .sub {
      display: block;
      font-weight: normal;
      font-size: 13px;
      color: var(--themeColorDarkerThree, #555);
    }
  `;
  @property({ type: Array })
  candidates: IssueEscalationCandidate[] = [];
  @property({ type: Boolean })
  enabled = false;
  @property({ type: String })
  href = '';

  private get overdue() {
    if (!this.enabled) {
      return [];
    }
    return overdueIssues(this.candidates, LocalDate.now());
  }

  private onClick(e: Event) {
    e.preventDefault();
    this.dispatchEvent(new CustomEvent('navigate', { bubbles: true, composed: true, detail: { href: this.href } }));
  }

  render() {
    const overdue = this.overdue;
    if (overdue.length === 0) {
      return nothing;
    }
    return html`
      <a class="widget" href=${this.href} @click=${(e: Event) => this.onClick(e)}>
        <div class="count">${overdue.length}</div>
        <div class="text">
          ${t('issues.escalation.widgetOverdue')}
          <span class="sub">${t('issues.escalation.widgetCta')}</span>
        </div>
      </a>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-issue-escalation-widget': DIssueEscalationWidget;
  }
}
